import React, { useState, useEffect, useRef, useCallback } from 'react';
import { authService } from '../../services/authService';
import { 
  validateUsername, 
  validateEmail, 
  validatePassword, 
  validatePasswordMatch,
  validateAccessKey,
  formatAccessKeyInput,
  getErrorMessage 
} from '../../utils/validators';
import Icon from '../Icon/Icon';
import PasswordStrengthBar from '../PasswordStrengthBar/PasswordStrengthBar';
import styles from './RegisterModal.module.css';

/**
 * RegisterModal Component - TECNILAND Registration Form
 * Modal para crear una nueva cuenta con Access Key
 * DESKTOP-FIRST: Optimizado para 1920px+
 */
function RegisterModal({ isOpen, onClose, onSwitchToLogin, onSuccess }) {
  const modalRef = useRef(null);
  const usernameInputRef = useRef(null);
  const previousFocusRef = useRef(null);

  // Estado del formulario
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    accessKey: '',
    acceptTerms: false
  });
  
  // Estado de errores
  const [errors, setErrors] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    accessKey: '',
    acceptTerms: '',
    general: ''
  });

  // Estado de validación en tiempo real
  const [validation, setValidation] = useState({
    username: null, // null = no validado, true = válido, false = inválido
    email: null,
    password: null,
    confirmPassword: null,
    accessKey: null
  });

  // Fortaleza de contraseña
  const [passwordStrength, setPasswordStrength] = useState({
    strength: 0,
    checks: {}
  });
  
  // Estado de carga
  const [isLoading, setIsLoading] = useState(false);
  
  // Éxito de registro
  const [isSuccess, setIsSuccess] = useState(false);

  // Auto-focus en el input al abrir el modal
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement;
      
      // Bloquear scroll del body
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      
      // Focus al input después de que se renderice
      setTimeout(() => {
        if (usernameInputRef.current) {
          usernameInputRef.current.focus();
        }
      }, 100);
      
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // Cerrar con ESC
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Restaurar focus al cerrar
  useEffect(() => {
    if (!isOpen && previousFocusRef.current) {
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
  }, [isOpen]);

  // Reset formulario al cerrar
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        accessKey: '',
        acceptTerms: false
      });
      setErrors({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        accessKey: '',
        acceptTerms: '',
        general: ''
      });
      setValidation({
        username: null,
        email: null,
        password: null,
        confirmPassword: null,
        accessKey: null
      });
      setPasswordStrength({ strength: 0, checks: {} });
      setIsSuccess(false);
    }
  }, [isOpen]);

  // Validar un campo específico
  const validateFieldHandler = useCallback((name, value, currentFormData) => {
    let result;
    
    switch (name) {
      case 'username':
        result = validateUsername(value);
        setValidation(prev => ({ ...prev, username: value ? result.valid : null }));
        if (!result.valid && value) {
          setErrors(prev => ({ ...prev, username: result.error }));
        }
        break;
        
      case 'email':
        result = validateEmail(value);
        setValidation(prev => ({ ...prev, email: value ? result.valid : null }));
        if (!result.valid && value) {
          setErrors(prev => ({ ...prev, email: result.error }));
        }
        break;
        
      case 'password':
        result = validatePassword(value);
        setValidation(prev => ({ ...prev, password: value ? result.valid : null }));
        setPasswordStrength({ strength: result.strength, checks: result.checks });
        if (!result.valid && value) {
          setErrors(prev => ({ ...prev, password: result.error }));
        }
        // Re-validar confirmPassword si ya tiene valor
        if (currentFormData?.confirmPassword) {
          const matchResult = validatePasswordMatch(value, currentFormData.confirmPassword);
          setValidation(prev => ({ ...prev, confirmPassword: matchResult.valid }));
          if (!matchResult.valid) {
            setErrors(prev => ({ ...prev, confirmPassword: matchResult.error }));
          }
        }
        break;
        
      case 'confirmPassword':
        result = validatePasswordMatch(currentFormData?.password || '', value);
        setValidation(prev => ({ ...prev, confirmPassword: value ? result.valid : null }));
        if (!result.valid && value) {
          setErrors(prev => ({ ...prev, confirmPassword: result.error }));
        }
        break;
        
      case 'accessKey':
        result = validateAccessKey(value);
        setValidation(prev => ({ ...prev, accessKey: value.length > 5 ? result.valid : null }));
        if (!result.valid && value.length > 5) {
          setErrors(prev => ({ ...prev, accessKey: result.error }));
        }
        break;
        
      default:
        break;
    }
  }, []);

  // Manejar cambios en inputs con validación en tiempo real
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === 'checkbox' ? checked : value;
    
    // Auto-formatear access key
    if (name === 'accessKey') {
      newValue = formatAccessKeyInput(value);
    }
    
    setFormData(prev => {
      const newFormData = {
        ...prev,
        [name]: newValue
      };
      
      // Validación en tiempo real (pasando el nuevo formData)
      validateFieldHandler(name, newValue, newFormData);
      
      return newFormData;
    });
    
    // Limpiar error del campo
    setErrors(prev => ({
      ...prev,
      [name]: '',
      general: ''
    }));
  }, [validateFieldHandler]);

  // Validar todo el formulario
  const validateForm = useCallback(() => {
    const newErrors = {};
    let isValid = true;
    
    // Username
    const usernameResult = validateUsername(formData.username);
    if (!usernameResult.valid) {
      newErrors.username = usernameResult.error;
      isValid = false;
    }
    
    // Email
    const emailResult = validateEmail(formData.email);
    if (!emailResult.valid) {
      newErrors.email = emailResult.error;
      isValid = false;
    }
    
    // Password
    const passwordResult = validatePassword(formData.password);
    if (!passwordResult.valid) {
      newErrors.password = passwordResult.error;
      isValid = false;
    }
    
    // Confirm Password
    const confirmResult = validatePasswordMatch(formData.password, formData.confirmPassword);
    if (!confirmResult.valid) {
      newErrors.confirmPassword = confirmResult.error;
      isValid = false;
    }
    
    // Access Key
    const accessKeyResult = validateAccessKey(formData.accessKey);
    if (!accessKeyResult.valid) {
      newErrors.accessKey = accessKeyResult.error;
      isValid = false;
    }
    
    // Terms
    if (!formData.acceptTerms) {
      newErrors.acceptTerms = 'Debes aceptar los términos y condiciones';
      isValid = false;
    }
    
    setErrors(prev => ({ ...prev, ...newErrors }));
    
    return isValid;
  }, [formData]);

  // Manejar submit
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    setErrors(prev => ({ ...prev, general: '' }));
    
    try {
      const response = await authService.register(
        formData.username,
        formData.email,
        formData.password,
        formData.accessKey
      );
      
      if (response.success) {
        // Registro exitoso
        setIsSuccess(true);
        
        // Llamar callback de éxito si existe
        if (onSuccess) {
          onSuccess(response.data.user);
        }
      } else {
        // Error del backend
        const errorMessage = getErrorMessage(response.code, response.error);
        
        // Asignar error al campo correspondiente
        switch (response.code) {
          case 'INVALID_USERNAME':
          case 'USERNAME_TAKEN':
            setErrors(prev => ({ ...prev, username: errorMessage }));
            setValidation(prev => ({ ...prev, username: false }));
            break;
          case 'INVALID_EMAIL':
          case 'EMAIL_TAKEN':
            setErrors(prev => ({ ...prev, email: errorMessage }));
            setValidation(prev => ({ ...prev, email: false }));
            break;
          case 'INVALID_PASSWORD':
            setErrors(prev => ({ ...prev, password: errorMessage }));
            setValidation(prev => ({ ...prev, password: false }));
            break;
          case 'INVALID_KEY_FORMAT':
          case 'KEY_NOT_FOUND':
          case 'KEY_ALREADY_USED':
          case 'ACCESS_KEY_REQUIRED':
            setErrors(prev => ({ ...prev, accessKey: errorMessage }));
            setValidation(prev => ({ ...prev, accessKey: false }));
            break;
          default:
            setErrors(prev => ({ ...prev, general: errorMessage }));
        }
      }
    } catch (error) {
      console.error('Error en registro:', error);
      setErrors(prev => ({ 
        ...prev, 
        general: 'Error de conexión con el servidor' 
      }));
    } finally {
      setIsLoading(false);
    }
  }, [formData, validateForm, onSuccess]);

  // Cerrar al hacer click fuera del modal
  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  // Cambiar a login
  const handleSwitchToLogin = useCallback((e) => {
    e.preventDefault();
    onSwitchToLogin();
  }, [onSwitchToLogin]);

  // Función para obtener clase de validación
  const getValidationClass = useCallback((fieldName) => {
    if (validation[fieldName] === true) return styles.inputValid;
    if (validation[fieldName] === false || errors[fieldName]) return styles.inputError;
    return '';
  }, [validation, errors]);

  // No renderizar si no está abierto
  if (!isOpen) return null;

  // Vista de éxito
  if (isSuccess) {
    return (
      <div 
        className={styles.modalBackdrop} 
        onClick={handleBackdropClick}
        role="dialog"
        aria-modal="true"
      >
        <div className={styles.modalContent}>
          <div className={styles.successContent}>
            <div className={styles.successIcon}>
              <Icon name="check" size="xl" />
            </div>
            <h2 className={styles.successTitle}>¡Cuenta creada!</h2>
            <p className={styles.successText}>
              Tu cuenta ha sido creada exitosamente. Ya puedes iniciar sesión con tus credenciales.
            </p>
            <button
              type="button"
              className={styles.submitButton}
              onClick={handleSwitchToLogin}
            >
              <Icon name="login" size="sm" />
              <span>Iniciar Sesión</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={styles.modalBackdrop} 
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="register-title"
    >
      <div className={styles.modalContent} ref={modalRef}>
        {/* Botón cerrar */}
        <button 
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Cerrar"
          type="button"
        >
          <Icon name="close" size="sm" />
        </button>

        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.iconWrapper}>
            <Icon name="profile" size="lg" animation="pulse" />
          </div>
          <h2 id="register-title" className={styles.title}>Crear Cuenta</h2>
          <p className={styles.subtitle}>Únete a la comunidad TECNILAND</p>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} className={styles.form}>
          {/* Error general */}
          {errors.general && (
            <div className={styles.errorGeneral}>
              <Icon name="alert" size="sm" />
              <span>{errors.general}</span>
            </div>
          )}

          {/* Campo Username */}
          <div className={styles.inputGroup}>
            <label htmlFor="register-username" className={styles.label}>
              Nombre de usuario
            </label>
            <div className={`${styles.inputWrapper} ${getValidationClass('username')}`}>
              <Icon name="profile" size="sm" />
              <input
                ref={usernameInputRef}
                type="text"
                id="register-username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                placeholder="3-16 caracteres"
                autoComplete="username"
                disabled={isLoading}
                className={styles.input}
                maxLength={16}
              />
              {validation.username === true && (
                <span className={styles.validIcon}>✓</span>
              )}
              {validation.username === false && (
                <span className={styles.invalidIcon}>✗</span>
              )}
            </div>
            {errors.username && (
              <span className={styles.errorText}>{errors.username}</span>
            )}
          </div>

          {/* Campo Email */}
          <div className={styles.inputGroup}>
            <label htmlFor="register-email" className={styles.label}>
              Email
            </label>
            <div className={`${styles.inputWrapper} ${getValidationClass('email')}`}>
              <Icon name="contact" size="sm" />
              <input
                type="email"
                id="register-email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="tu@email.com"
                autoComplete="email"
                disabled={isLoading}
                className={styles.input}
              />
              {validation.email === true && (
                <span className={styles.validIcon}>✓</span>
              )}
              {validation.email === false && (
                <span className={styles.invalidIcon}>✗</span>
              )}
            </div>
            {errors.email && (
              <span className={styles.errorText}>{errors.email}</span>
            )}
          </div>

          {/* Campo Password */}
          <div className={styles.inputGroup}>
            <label htmlFor="register-password" className={styles.label}>
              Contraseña
            </label>
            <div className={`${styles.inputWrapper} ${getValidationClass('password')}`}>
              <Icon name="shield" size="sm" />
              <input
                type="password"
                id="register-password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Mínimo 8 caracteres"
                autoComplete="new-password"
                disabled={isLoading}
                className={styles.input}
              />
              {validation.password === true && (
                <span className={styles.validIcon}>✓</span>
              )}
            </div>
            {/* Barra de fortaleza */}
            {formData.password && (
              <PasswordStrengthBar 
                strength={passwordStrength.strength}
                checks={passwordStrength.checks}
              />
            )}
            {errors.password && (
              <span className={styles.errorText}>{errors.password}</span>
            )}
          </div>

          {/* Campo Confirmar Password */}
          <div className={styles.inputGroup}>
            <label htmlFor="register-confirm-password" className={styles.label}>
              Confirmar Contraseña
            </label>
            <div className={`${styles.inputWrapper} ${getValidationClass('confirmPassword')}`}>
              <Icon name="shield" size="sm" />
              <input
                type="password"
                id="register-confirm-password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Repite tu contraseña"
                autoComplete="new-password"
                disabled={isLoading}
                className={styles.input}
              />
              {validation.confirmPassword === true && (
                <span className={styles.validIcon}>✓</span>
              )}
              {validation.confirmPassword === false && (
                <span className={styles.invalidIcon}>✗</span>
              )}
            </div>
            {errors.confirmPassword && (
              <span className={styles.errorText}>{errors.confirmPassword}</span>
            )}
          </div>

          {/* Campo Access Key */}
          <div className={styles.inputGroup}>
            <div className={styles.labelWithTooltip}>
              <label htmlFor="register-access-key" className={styles.label}>
                Llave de Acceso
              </label>
              <div className={styles.tooltip}>
                <Icon name="info" size="xs" />
                <span className={styles.tooltipText}>
                  Las llaves de acceso son proporcionadas por el servidor para poder registrarse
                </span>
              </div>
            </div>
            <div className={`${styles.inputWrapper} ${getValidationClass('accessKey')}`}>
              <Icon name="key" size="sm" />
              <input
                type="text"
                id="register-access-key"
                name="accessKey"
                value={formData.accessKey}
                onChange={handleInputChange}
                placeholder="TECNI-XXXX-XXXX-XXXX-XXXX"
                disabled={isLoading}
                className={styles.input}
                maxLength={25}
              />
              {validation.accessKey === true && (
                <span className={styles.validIcon}>✓</span>
              )}
              {validation.accessKey === false && (
                <span className={styles.invalidIcon}>✗</span>
              )}
            </div>
            {errors.accessKey && (
              <span className={styles.errorText}>{errors.accessKey}</span>
            )}
          </div>

          {/* Checkbox términos */}
          <div className={styles.checkboxGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                name="acceptTerms"
                checked={formData.acceptTerms}
                onChange={handleInputChange}
                disabled={isLoading}
                className={styles.checkbox}
              />
              <span className={styles.checkboxCustom}></span>
              <span>
                Acepto los{' '}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.termsLink}
                  onClick={(e) => e.stopPropagation()}
                >
                  términos y condiciones
                </a>
                {' '}y la{' '}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.termsLink}
                  onClick={(e) => e.stopPropagation()}
                >
                  política de privacidad
                </a>
              </span>
            </label>
            {errors.acceptTerms && (
              <span className={styles.errorText}>{errors.acceptTerms}</span>
            )}
          </div>

          {/* Botón submit */}
          <button
            type="submit"
            className={styles.submitButton}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className={styles.loadingSpinner}></span>
                <span>Creando cuenta...</span>
              </>
            ) : (
              <>
                <Icon name="register" size="sm" />
                <span>Crear Cuenta</span>
              </>
            )}
          </button>
        </form>

        {/* Footer - Cambiar a login */}
        <div className={styles.modalFooter}>
          <p>¿Ya tienes cuenta?</p>
          <button 
            type="button"
            className={styles.switchLink}
            onClick={handleSwitchToLogin}
          >
            Inicia Sesión aquí
          </button>
        </div>

        {/* Decoración de fondo */}
        <div className={styles.modalDecoration}></div>
      </div>
    </div>
  );
}

export default RegisterModal;
